import { useState, useEffect, useRef } from 'react';
import { trackingAPI } from '../services/api';

/**
 * Custom hook for shipment tracking state management
 */
const useShipmentTracking = (shipmentId) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  /**
   * Fetch all tracking data
   */
  const fetchTracking = async () => {
    if (!shipmentId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await trackingAPI.getTracking(shipmentId);
      setTracking(response.data);
    } catch (err) {
      console.error('Error fetching tracking:', err);
      const errorMessage = err.response?.data?.error || 'Failed to load tracking data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add a manual tracking event
   */
  const addEvent = async (eventType, data = {}) => {
    try {
      setLoading(true);
      setError(null);

      await trackingAPI.addEvent(shipmentId, { eventType, ...data });

      // Refetch tracking data
      await fetchTracking();

      return true;
    } catch (err) {
      console.error('Error adding event:', err);
      const errorMessage = err.response?.data?.error || 'Failed to add event';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and set up auto-refresh
  useEffect(() => {
    if (shipmentId) {
      fetchTracking();

      // Auto-refresh every 5 minutes
      intervalRef.current = setInterval(fetchTracking, 5 * 60 * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [shipmentId]);

  return {
    tracking,
    milestones: tracking?.milestones || [],
    events: tracking?.events || [],
    updates: tracking?.updates || [],
    eta: tracking?.eta || null,
    delays: tracking?.delays || null,
    loading,
    error,
    fetchTracking,
    addEvent
  };
};

export default useShipmentTracking;
